// [<agent>] > [Prompt] - where an operator owns one agent's instructions.
// Modelled on BMA's "Research prompts" card: versioned, append-only,
// restore-by-copy, server-computed diffs.
//
// The prompt is scaffold + addendum. Only the addendum is editable; the scaffold
// is shown read-only so the addendum is not written blind. See
// docs/Y2026-Q3/design-rule-based-operator-prompts.md.
//
// The agent comes from the URL (StatefulAgentsLayout owns the selector).
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  type RuleChange,
  useStatefulAgentPrompt,
  useStatefulAgents,
  useRestorePrompt,
  usePromptDiff,
} from "../api/hooks/useStatefulAgents";
import { AgentFeedbackSection } from "../components/AgentFeedbackSection";
import { DiffView } from "../components/DiffView";
import { errorText, PromptRulesEditor } from "./PromptRulesEditor";
import styles from "./StatefulAgentsRoute.module.css";

const formatWhen = (iso: string): string =>
  new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const ORIGIN_LABEL: Record<string, string> = {
  seed: "written by engineering",
  manual: "saved by an operator",
  restore: "restored",
  agent_proposal: "proposed by the agent",
  // Published from the code seed onto rules nobody here had edited, at a run
  // launch after a deploy. An ordinary revision: diff it, restore past it.
  seed_sync: "applied from the code seed",
  reseed: "re-published from the code seed",
};

const CHANGE_VERB: Record<RuleChange["kind"], string> = {
  added: "Added",
  removed: "Deleted",
  changed: "Updated",
  status: "Status of",
  reordered: "Reordered",
};

// The rule-level view of a diff. On a 27-rule playbook a character diff answers
// a question nobody asked, so this leads and the text diff sits under it.
export const RuleChangeList = ({
  changes,
  preambleChanged,
}: {
  changes: RuleChange[];
  preambleChanged: boolean;
}) => {
  if (changes.length === 0 && !preambleChanged) {
    return <p className={styles.muted}>No difference.</p>;
  }
  return (
    <ul className={styles.changeList}>
      {preambleChanged && <li>Edited the framing</li>}
      {changes.map((change) => (
        <li key={`${change.kind}-${change.rule_id}`}>
          <strong>
            {CHANGE_VERB[change.kind]} {change.rule_id}
          </strong>
          {change.title ? ` - ${change.title}` : ""}
          {change.detail ? ` (${change.detail})` : ""}
          {change.fields_changed.length > 0 && change.kind === "changed"
            ? ` (${change.fields_changed.join(", ")})`
            : ""}
        </li>
      ))}
    </ul>
  );
};

export const AgentPromptTab = () => {
  const { user } = useKindeAuth();
  const actorEmail = user?.email ?? "";
  const { agent: agentParam } = useParams();
  const agent = agentParam ?? null;

  const promptQuery = useStatefulAgentPrompt(agent);
  // A document every agent reads, rather than one agent's own instructions.
  const sharedDocument = (useStatefulAgents().data?.documents ?? []).find(
    (d) => d.agent === agent,
  );
  const shared = sharedDocument !== undefined;
  const detail = promptQuery.data;
  const restore = useRestorePrompt(agent ?? "");

  const [scaffoldOpen, setScaffoldOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [diffFrom, setDiffFrom] = useState<number | null>(null);
  const [diffTo, setDiffTo] = useState<number | null>(null);
  // Whose revisions the pickers hold. Switching agents changes the route at once but
  // the pickers only on the next render, and in between the old agent's revision
  // numbers were sent for the new one (a 400 on /prompt/diff).
  const [diffAgent, setDiffAgent] = useState<string | null>(null);

  // Re-point the comparison whenever the agent or its ACTIVE revision changes (a
  // save landing), so it always opens on "what did the last edit do?".
  const activeRevision = detail?.active.revision;
  useEffect(() => {
    if (activeRevision === undefined) {
      return;
    }
    setDiffAgent(agent);
    setDiffTo(activeRevision);
    setDiffFrom(activeRevision > 1 ? activeRevision - 1 : null);
  }, [agent, activeRevision]);

  const diffQuery = usePromptDiff(
    diffAgent === agent ? agent : null,
    diffFrom,
    diffTo,
  );

  if (promptQuery.isLoading) {
    return <p className={styles.muted}>Loading instructions…</p>;
  }
  if (promptQuery.error) {
    return (
      <p className={styles.error}>Could not load this agent's instructions.</p>
    );
  }

  return (
    <div className={styles.tab}>
      <p className={styles.muted}>
        {!shared
          ? "This agent runs on instructions you own. "
          : sharedDocument.read_by_agents
            ? "Every agent reads this document, and any agent can change it the way it changes its own rules. "
            : "No agent reads this document yet: it is the list next actions will be filed under once agents keep them. "}
        Saving writes a new revision - nothing is ever edited in place, so the
        instructions behind any past run stay recoverable.
      </p>

      {detail && (
        <>
          <p className={styles.description}>{detail.description}</p>
          <div className={styles.activeHeader}>
            Revision {detail.active.revision}
            {detail.active.created_by_email
              ? ` · saved by ${detail.active.created_by_email}`
              : " · written by engineering"}{" "}
            · {formatWhen(detail.active.created_at)}
            {detail.active.note ? ` · "${detail.active.note}"` : ""}
          </div>

          {/* The behavior questions whose answers would change this prompt. */}
          {agent && (
            <AgentFeedbackSection scope={{ prompt_document: agent }} showProjects />
          )}

          {/* Keyed on the agent: react-query serves a previously-fetched
              agent from cache synchronously, so without this the editor is not
              remounted on a switch and one agent's unsaved draft can be saved
              onto another. */}
          <PromptRulesEditor
            key={agent}
            detail={detail}
            actorEmail={actorEmail}
          />
          {restore.error && (
            <p className={styles.error}>
              {/* A 409 means another write landed since this tab loaded; the prompt
                  is refetched, so the next Restore goes against the current one. */}
              Could not restore:{" "}
              {errorText(
                restore.error,
                "another change landed first. The prompt has been refreshed - Restore again.",
              )}
            </p>
          )}

          {detail.scaffold_prompt && (
          <details
            className={styles.scaffold}
            open={scaffoldOpen}
            onToggle={(e) =>
              setScaffoldOpen((e.target as HTMLDetailsElement).open)
            }
          >
            <summary>
              Fixed instructions (written in code, not editable) - your rules
              above are appended to this
            </summary>
            <pre className={styles.scaffoldText}>{detail.scaffold_prompt}</pre>
          </details>
          )}

          <details
            className={styles.scaffold}
            open={previewOpen}
            onToggle={(e) =>
              setPreviewOpen((e.target as HTMLDetailsElement).open)
            }
          >
            <summary>
              Preview - exactly what the agent receives from your rules
            </summary>
            <pre className={styles.scaffoldText}>
              {detail.active.prompt_text}
            </pre>
          </details>

          <h2 className={styles.sectionTitle}>History</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Revision</th>
                <th>When</th>
                <th>Who</th>
                <th>Origin</th>
                <th>Rules</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {detail.revisions.map((r) => (
                <tr key={r.revision}>
                  <td>r{r.revision}</td>
                  <td>{formatWhen(r.created_at)}</td>
                  <td>{r.created_by_email || "engineering"}</td>
                  <td>
                    {ORIGIN_LABEL[r.origin_kind] ?? r.origin_kind}
                    {r.origin_revision != null
                      ? ` from r${r.origin_revision}`
                      : ""}
                  </td>
                  <td>{r.rule_count}</td>
                  <td className={styles.noteCell}>{r.note}</td>
                  <td className={styles.rowActions}>
                    <button
                      type="button"
                      className={styles.link}
                      onClick={() => {
                        setDiffFrom(r.revision);
                        setDiffTo(detail.active.revision);
                      }}
                    >
                      Diff vs active
                    </button>
                    {r.revision !== detail.active.revision && (
                      <button
                        type="button"
                        className={styles.link}
                        disabled={restore.isLoading}
                        onClick={() =>
                          restore.mutate({
                            revision: r.revision,
                            // The revision this tab is showing: if another write
                            // landed since, the restore is refused rather than
                            // silently reverting it.
                            base_revision: detail.active.revision,
                            note: `Restored revision ${r.revision}`,
                            actor_email: actorEmail,
                          })
                        }
                      >
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 className={styles.sectionTitle}>Compare</h2>
          <div className={styles.diffPickers}>
            <label>
              From
              <select
                value={diffFrom ?? ""}
                onChange={(e) =>
                  setDiffFrom(
                    e.target.value === "" ? null : Number(e.target.value),
                  )
                }
              >
                <option value="">(nothing)</option>
                {detail.revisions.map((r) => (
                  <option key={r.revision} value={r.revision}>
                    r{r.revision}
                  </option>
                ))}
              </select>
            </label>
            <label>
              To
              <select
                value={diffTo ?? ""}
                onChange={(e) => setDiffTo(Number(e.target.value))}
              >
                {detail.revisions.map((r) => (
                  <option key={r.revision} value={r.revision}>
                    r{r.revision}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {diffQuery.data && (
            <>
              <RuleChangeList
                changes={diffQuery.data.rule_changes}
                preambleChanged={diffQuery.data.preamble_changed}
              />
              <DiffView lines={diffQuery.data.lines} />
            </>
          )}
        </>
      )}
    </div>
  );
};
